import 'Styles/BLHelpRequestForm.scss';
import { Heading } from '@lmig/lmds-react-typography';
import { Form } from '@lmig/lmds-react-form';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { FieldGroup, Message } from '@lmig/lmds-react-field-group';
import { Button } from '@lmig/lmds-react-button';
import { Textarea } from '@lmig/lmds-react-textarea';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useState } from 'react';
import { IconEmail, IconPhone } from '@lmig/lmds-react-icons';
import { Link } from '@lmig/lmds-react-link';

type BLHelpRequest = {
  name?: string;
  userID?: string;
  phone?: string;
  email?: string;
  agencyName?: string;
  agencyCode?: string;
  policyNumber?: string;
  description?: string;
};

type MJMLTemplate = {
  Name?: string;
  UserID?: string;
  AgencyName?: string;
  AgencyCode?: string;
  Email?: string;
  Phone?: string;
  Description?: string;
  AQPNumber?: string;
};

function BLHelpRequestForm() {
  const { userData } = useUserData();
  const [form, setForm] = useState<BLHelpRequest>({});
  const [error, setError] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<Message[]>([]);
  const [name, setName] = useState<string>('');
  const [userID, setUserID] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [agencyName, setAgencyName] = useState<string>('');
  const [agencyCode, setAgencyCode] = useState<string>('');
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>();
  const aisgEmail = 'AgencyInterfaceSupport@LibertyMutual.com';
  const aisgSmallandMidMarketPhone = '888-451-8414';
  const aisgNationalInsuranceSpecialtyPhone = '800-747-9742';

  const sendEmail = async () => {
    if (userData) {
      setSending(true);
      let mjml: MJMLTemplate = {
        Name: form.name,
        UserID: form.userID,
        AgencyName: form.agencyName,
        AgencyCode: form.agencyCode,
        Email: form.email,
        Phone: form.phone,
        Description: form.description,
        AQPNumber: form.policyNumber,
      };

      await fetch('/api/sitecore/Messaging/Email', {
        method: 'POST',
        body: new URLSearchParams({ template: 'help.mjml', mjml: JSON.stringify(mjml), subject: 'Help Request Form' }),
      });
      setSending(false);
      setSent(true);
    }
  };

  const requestAccess = () => {
    setErrorMessages([]);

    if (
      !form.name ||
      !form.userID ||
      !form.phone ||
      (form?.phone?.length && form?.phone?.length < 14) ||
      !form.email ||
      !form.agencyName ||
      !form.agencyCode ||
      !form.description ||
      (form?.policyNumber?.length && form.policyNumber.length > 20)
    ) {
      setError(true);
    } else sendEmail();

    if (!form.name)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Name is required' }]);
    if (!form.userID)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'UserID is required' }]);
    if (!form.phone)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Phone number is required' }]);
    if (form.phone && form?.phone?.length && form?.phone?.length < 14) {
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Phone must have 10 digits' }]);
    }
    if (!form.email)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Email is required' }]);
    if (!form.agencyName)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Agency Name is required' }]);
    if (!form.agencyCode)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Agency Code is required' }]);
    if (form?.policyNumber?.length && form.policyNumber.length > 20) {
      setErrorMessages((errorMessages) => [
        ...errorMessages,
        { type: 'error', text: 'Application / Quote / Policy Number must be 20 characters or less' },
      ]);
    }
    if (!form.description)
      setErrorMessages((errorMessages) => [...errorMessages, { type: 'error', text: 'Description is required' }]);
  };

  useEffect(() => {
    setForm((form) => ({
      ...form,
      name: name,
      userID: userID,
      phone: phone,
      email: email,
      agencyName: agencyName,
      agencyCode: agencyCode,
      policyNumber: policyNumber,
    }));
  }, [name, userID, phone, email, agencyName, agencyCode, policyNumber]);

  useEffect(() => {
    if (sent) setSent(false);
    if (!error) {
      setErrorMessages([]);
      setError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    if (userData) {
      setName(userData.name ?? '');
      setUserID(userData.userId?.split('@ciportal')[0] ?? '');
      setEmail(userData.email ?? '');
      setAgencyName(userData.selectedStatCodeData?.AgencyName ?? '');
      setAgencyCode(userData.selectedStatCodeData?.SubStat ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNameChange = (event: any) => {
    setName(event.currentTarget?.value);
  };

  const onUserIDChange = (event: any) => {
    setUserID(event.currentTarget?.value);
  };

  const onPhoneChange = (event: any) => {
    setPhone(event.currentTarget?.value);
  };

  const onEmailChange = (event: any) => {
    setEmail(event.currentTarget?.value);
  };

  const onAgencyNameChange = (event: any) => {
    setAgencyName(event.currentTarget?.value);
  };

  const onAgencyCodeChange = (event: any) => {
    setAgencyCode(event.currentTarget?.value);
  };

  const onPolicyNumberChange = (event: any) => {
    setPolicyNumber(event.currentTarget?.value);
  };

  return (
    <div>
      <Form id="bl-help-request-form">
        <Heading type="h2-light">Contact us</Heading>
        <div className="form-box top">
          <div className="form-top-section">
            <div className="sub-header">Contact AISG</div>
            <div className="form-caption bl-help-request-form-bold add-bottom-margin">
              The Agency Interface Support Group (AISG) is available to help with issues related to our portals,
              quoting, and servicing applications.
            </div>
            <div className="contact">
              <div className="info first-column">
                <div className="phone">
                  <IconPhone size="16" className="icon"></IconPhone>
                  Call
                </div>
                <div className="group bl-help-request-form-bold">
                  <div>Monday - Friday</div>
                  <div>8 AM to 8 PM ET</div>
                </div>
                <div className="group bl-help-request-form-bold">
                  <div>Small and Mid Markets</div>
                  <Link href={`tel:${aisgSmallandMidMarketPhone}`} variant="standalone" caretPosition="none">
                    {aisgSmallandMidMarketPhone}
                  </Link>
                </div>
                <div className="group bl-help-request-form-bold">
                  <div>National Insurance Specialty</div>
                  <Link href={`tel:${aisgNationalInsuranceSpecialtyPhone}`} variant="standalone" caretPosition="none">
                    {aisgNationalInsuranceSpecialtyPhone}
                  </Link>
                </div>
              </div>
              <div className="info">
                <div className="email">
                  <IconEmail size="16" className="icon"></IconEmail>
                  Email
                </div>
                <div className="group bl-help-request-form-bold">
                  <Link href={`mailto:${aisgEmail}`} variant="standalone" caretPosition="none">
                    {aisgEmail}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-box">
          <div className="form-top-section">
            <div className="form-caption">Submit a support request</div>
          </div>
          <FieldGroup id="bl-help-request-fieldgroup" messages={errorMessages}>
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Your name"
              value={name}
              highlightType={error && !form?.name ? 'error' : undefined}
              onChange={(event) => onNameChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="User ID"
              value={userID}
              highlightType={error && !form?.userID ? 'error' : undefined}
              onChange={(event) => onUserIDChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Phone number"
              mask="phone"
              value={phone}
              highlightType={error && !form?.phone ? 'error' : undefined}
              onChange={(event) => onPhoneChange(event)}
              maxLength={14}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Email"
              value={email}
              highlightType={error && !form?.email ? 'error' : undefined}
              onChange={(event) => onEmailChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Agency name"
              value={agencyName}
              highlightType={error && !form?.agencyName ? 'error' : undefined}
              onChange={(event) => onAgencyNameChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Agency code"
              value={agencyCode}
              highlightType={error && !form?.agencyCode ? 'error' : undefined}
              onChange={(event) => onAgencyCodeChange(event)}
            />
            <AlphanumericInput
              id="Alphanumeric--default"
              labelVisual="Quote / policy number (optional)"
              value={policyNumber}
              onChange={(event) => onPolicyNumberChange(event)}
              maxLength={20}
            />
          </FieldGroup>
          <Textarea
            id="Textarea--default"
            labelVisual="Short description of issue"
            value={form?.description}
            highlightType={error && !form?.description ? 'error' : undefined}
            onChange={(event) => setForm((form) => ({ ...form, description: event.target?.value }))}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          ></Textarea>

          <div className="bottom-row">
            <Button
              size="large"
              className="mail-to-link"
              variant="primary"
              loading={sending}
              disabled={sent}
              onClick={() => requestAccess()}
            >
              {sent ? 'Request Sent' : 'Send Request'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default BLHelpRequestForm;
